'use client';

import React, { Component, ReactElement } from 'react';
import ReactDOMServer from 'react-dom/server';
import {
  getPrimisIDbyChannel,
  getAMPPrimisIDbyChannel,
  getDisplayAdBlock,
  createCampaignifyBlock,
  getAdBlock,
} from '@benzinga/ads-utils';
import { Block, CampaignifyPlaceholderBlock, MoneyBlockType } from '@benzinga/content-manager';
import { sanitizeHTML } from '@benzinga/frontend-utils';
import {
  ArticleBlock,
  ArticleBlockPlaceholder,
  ArticleBlocksList,
  Campaigns,
  CampaignStrategy,
  countElementsInBodyBlocks,
  findPlaceholderPlaceInBlocks,
  findPlaceholderPositionInParsedContent,
  getPrimisIdByTaxonomies,
  injectNodeIdInCampaignBlocks,
  insertCampaign,
  insertCampaignInBlocks,
  isTaxonomyExists,
  performRegexesOnCampaignHTML,
} from '@benzinga/article-manager';
import { StoryCategory } from '@benzinga/advanced-news-manager';
import { DateTime } from 'luxon';

import styles from './Campaign.module.scss';

const Primis = React.lazy(() =>
  import('@benzinga/ads/primis').then(module => {
    return { default: module.Primis };
  }),
);

const GoogleAdUnit = React.lazy(() =>
  import('@benzinga/ads/google-ad-unit').then(module => {
    return { default: module.GoogleAdUnit };
  }),
);

type Blocks = ArticleBlock[] | ArticleBlockPlaceholder[] | MoneyBlockType[];

export interface CampaignProps {
  nodeId?: number | string;
  contentType?: string;
  articleTitle?: string;
  articleBody: string[];
  articleBlocks?: Blocks;
  inContentBlocks?: Blocks;
  campaigns?: Campaigns;
  disableCampaignifyUnit?: boolean;
  hasAdLight?: boolean;
  isAmp?: boolean;
  isSponsored?: boolean;
  channels?: StoryCategory[];
  createdDate?: string;
  tags?: StoryCategory[];
  ticker?: string;
  strategy?: CampaignStrategy;
  onRendered?: () => void;
  primis?: boolean;
  wordCount?: number | null;
  googleAdPlaceholder?: Block;
  raptiveEnabled?: boolean;
  children(articleBody: string | ArticleBlock[] | ArticleBlockPlaceholder[] | MoneyBlockType[]): ReactElement;
}

const adBlocknames = ['ad/campaignify', 'acf/raptive-ad-placement', 'acf/freestar-ad-placement', 'ad/primis'];
//const additionalAdBlocks = ['acf/newsletter-subscription-box'];
const MIN_BLOCKS_BETWEEN_ADS = 4;
const NUMBER_OF_BODY_ADS = 3;
const MIN_PARAGRAPHS_BETWEEN_ADS_AND_IMAGES = 3;

const insertAt = <T,>(index: number, value: T, list: T[]): T[] => {
  const result = [...list];
  result.splice(index, 0, value);
  return result;
};

export class Campaign extends Component<CampaignProps> {
  // Optimizes component to never re render, because it is managed by external script(s)
  shouldComponentUpdate() {
    return true;
  }

  componentDidUpdate() {
    if (this.props.onRendered) {
      this.props.onRendered();
    }
  }

  componentDidMount() {
    if (this.props.onRendered) {
      this.props.onRendered();
    }
  }

  getPrimisIdByTaxonomies = (amp = false): string => {
    const taxonomies: StoryCategory[] = [];

    if (Array.isArray(this.props.channels)) {
      taxonomies.push(...this.props.channels);
    }

    if (Array.isArray(this.props.tags)) {
      taxonomies.push(...this.props.tags);
    }

    const taxonomiesNames = taxonomies.map(item => item.name);

    if (amp) {
      return getAMPPrimisIDbyChannel(taxonomiesNames);
    }

    return getPrimisIDbyChannel(taxonomiesNames);
  };

  hideCampaignByRules = (): boolean => {
    const { articleBlocks, channels, contentType, createdDate } = this.props;

    const CONTENT_TYPE_FOR_HIDE = 'story';
    const CHANNELS_FOR_HIDE = ['News', 'Markets'];
    const COUNT_OF_ELEMENT_FOR_HIDE = 4;
    const TYPE_OF_ELEMENT_FOR_HIDE = 'p';
    const DAYS_OF_PUBLISHED_FOR_HIDE = 30;

    const duration = DateTime.now().diff(DateTime.fromISO(createdDate || ''), ['days']);

    const hiddenForType = contentType === CONTENT_TYPE_FOR_HIDE;
    const hiddenForTaxonomies = isTaxonomyExists(CHANNELS_FOR_HIDE, channels || []);
    const hiddenForElementsCount =
      countElementsInBodyBlocks((articleBlocks as ArticleBlock[]) || [], TYPE_OF_ELEMENT_FOR_HIDE) >
      COUNT_OF_ELEMENT_FOR_HIDE;
    const hideInCauseOfPublished = duration.days > DAYS_OF_PUBLISHED_FOR_HIDE;

    return hiddenForType && hiddenForTaxonomies && hiddenForElementsCount && hideInCauseOfPublished;
  };

  shouldRenderCampaign = (content: string[] | Blocks): boolean => {
    const { isSponsored, strategy } = this.props;

    if (this.hideCampaignByRules()) {
      return false;
    }

    return !!(Array.isArray(content) && content.length && !isSponsored && strategy !== 'none');
  };

  formatBodyContent = (): string | Blocks => {
    const { articleBlocks, articleBody, inContentBlocks } = this.props;

    const blocks = articleBlocks?.slice();

    if (blocks) {
      const combinedBlocks = Array.isArray(inContentBlocks) ? [...blocks, ...inContentBlocks] : blocks;
      const shouldRenderCampaign = this.shouldRenderCampaign(blocks);
      if (shouldRenderCampaign) {
        const formattedArticleBlocks = this.formatArticleBlock(combinedBlocks as ArticleBlock[]);
        return formattedArticleBlocks;
      }
      return combinedBlocks;
    } else {
      return this.shouldRenderCampaign(articleBody) ? this.injectParsedArticleAds(articleBody) : articleBody.toString();
    }
  };

  injectAdsIntoHTMLLists = (articleBlocks: ArticleBlock[], adBlock: MoneyBlockType | null): ArticleBlock[] => {
    if (!adBlock) return articleBlocks;
    const unorderedList = (articleBlocks as ArticleBlock[]).filter(articleBlock => articleBlock?.tag === 'ul')[0];
    const listItems =
      Array.isArray(unorderedList?.childBlocks) &&
      (unorderedList?.childBlocks as ArticleBlock[]).filter(articleBlock => articleBlock?.tag === 'li');

    if (listItems) {
      const randomMidPoint = Math.floor(listItems.length / 2) - 1;
      const childBlocks = listItems?.[randomMidPoint]?.childBlocks;
      const isAdBlockAtPosition =
        Array.isArray(childBlocks) &&
        this.checkIfAdBlockIsAtPosition(childBlocks as MoneyBlockType[], childBlocks.length - 1);

      if (!isAdBlockAtPosition && Array.isArray(childBlocks)) {
        (listItems[randomMidPoint].childBlocks as MoneyBlockType[]).push(adBlock);
      }
    }

    return articleBlocks ?? [];
  };

  formatArticleBlock = (articleBlocks: ArticleBlock[]): string | Blocks => {
    const articleBlocksWithNoAds = articleBlocks.filter(item => {
      return item?.blockName && !adBlocknames.includes(item.blockName);
    });
    let newArticleBlocks: MoneyBlockType[] = [...articleBlocksWithNoAds] as MoneyBlockType[];
    const hasUnder200Words = this.checkIfShortArticleByWordCount();
    //const isShortByBlockCount = this.checkIfShortArticleByBlockCount(articleBlocks);

    const baseCampaignifyParams = {
      nodeId: this.props.nodeId ? Number(this.props.nodeId) : undefined,
      strategy: this.props.strategy,
      ticker: this.props.ticker,
    };

    let middleAd: MoneyBlockType | null = createCampaignifyBlock({
      ...baseCampaignifyParams,
      position: 'middle',
    });

    let bottomAd: MoneyBlockType | null = createCampaignifyBlock({
      ...baseCampaignifyParams,
      position: 'bottom',
    });

    if (this.props.disableCampaignifyUnit) {
      middleAd = null;
      bottomAd = null;
    }

    if (hasUnder200Words) {
      middleAd && newArticleBlocks.unshift(middleAd);
    } else {
      if (this.props.hasAdLight) {
        bottomAd && newArticleBlocks.push(bottomAd);
      } else {
        middleAd && newArticleBlocks.unshift(middleAd);
        newArticleBlocks = this.handleFormatLongArticle(newArticleBlocks);
      }
    }
    return newArticleBlocks;
  };

  handleFormatLongArticle = (newArticleBlocks: MoneyBlockType[]): MoneyBlockType[] => {
    newArticleBlocks = this.insertAdsIntoArticle(newArticleBlocks);
    return newArticleBlocks;
  };

  insertAdsIntoArticle = articleBlocks => {
    const adPositions = this.findOptimalAdPositions(articleBlocks, NUMBER_OF_BODY_ADS);

    // Insert ads from back to front to avoid index shifting
    for (let i = adPositions.length - 1; i >= 0; i--) {
      articleBlocks.splice(adPositions[i], 0, getDisplayAdBlock());
    }

    return articleBlocks;
  };

  findNearestAppropriatePosition = (
    articleBlocks: ArticleBlock[],
    targetIndex: number,
    lastAdPosition: number,
  ): number => {
    const searchRange = 10;
    const totalBlocks = articleBlocks.length;

    for (let i = 0; i < searchRange; i++) {
      if (this.isAppropriateForAdInsertion(articleBlocks, targetIndex)) {
        return targetIndex;
      }

      // Check forward
      if (
        targetIndex + i < totalBlocks &&
        targetIndex + i - lastAdPosition >= MIN_BLOCKS_BETWEEN_ADS &&
        this.isAppropriateForAdInsertion(articleBlocks, targetIndex + i)
      ) {
        return targetIndex + i;
      }
      // Check backward
      if (
        targetIndex - i >= 0 &&
        targetIndex - i - lastAdPosition >= MIN_BLOCKS_BETWEEN_ADS &&
        this.isAppropriateForAdInsertion(articleBlocks, targetIndex - i)
      ) {
        return targetIndex - i;
      }
    }
    return -1;
  };

  findOptimalAdPositions = (articleBlocks: ArticleBlock[], numAds: number) => {
    const totalBlocks = articleBlocks.length;
    const adPositions: number[] = [];
    let lastAdPosition = -MIN_BLOCKS_BETWEEN_ADS;

    // Find the first suitable position after the 4rd block
    for (let i = 4; i < Math.min(articleBlocks.length, 4 + MIN_BLOCKS_BETWEEN_ADS); i++) {
      if (this.isAppropriateForAdInsertion(articleBlocks, i, true)) {
        adPositions.push(i);
        lastAdPosition = i;
        break;
      }
    }

    // Find positions for remaining ads
    while (adPositions.length < numAds && lastAdPosition < totalBlocks) {
      const nextPosition = this.findNearestAppropriatePosition(
        articleBlocks,
        lastAdPosition + MIN_BLOCKS_BETWEEN_ADS,
        lastAdPosition,
      );
      if (nextPosition !== -1 && nextPosition < totalBlocks) {
        adPositions.push(nextPosition);
        lastAdPosition = nextPosition;
      } else {
        break;
      }
    }

    return adPositions;
  };

  isAppropriateForAdInsertion = (articleBlocks: ArticleBlock[], index: number, isFirstAd = false): boolean => {
    const block = articleBlocks[index];
    if (!block?.innerHTML) return false;

    if (isFirstAd) {
      // if (this.isNearImageOrEmbedOrAd(articleBlocks, index, 3, true, true)) {
      //   return false;
      // }
      if (this.isNearHeader(articleBlocks, index, 1, true, false)) {
        return false;
      }

      return true;
    }

    // Avoid placing ads near images or other ad-like content
    if (this.isNearImageOrEmbedOrAd(articleBlocks, index, MIN_PARAGRAPHS_BETWEEN_ADS_AND_IMAGES)) {
      return false;
    }

    // Avoid placing ads near headers
    if (this.isNearHeader(articleBlocks, index, 1, true, false)) {
      return false;
    }

    // Avoid placing ads near horizontal rules
    if (this.isNearHorizontalRule(articleBlocks, index, 1, true, true)) {
      return false;
    }

    // Avoid interrupting lists, embedded content, or short paragraphs
    if ((block.tag && ['ul', 'ol', 'hr', 'figure'].includes(block.tag)) || block.innerHTML.length < 150) {
      return false;
    }

    // Check if we're near the end of the article
    const remainingBlocks = articleBlocks.length - index;
    if (remainingBlocks < 3) {
      return false;
    }

    return true;
  };

  isNearImageOrEmbedOrAd = (
    articleBlocks: ArticleBlock[],
    index: number,
    range: number,
    checkBefore = true,
    checkAfter = true,
    type: 'all' | 'image' | 'embed' | 'ad' = 'all',
  ): boolean => {
    const imageOrAdCheck = (block: ArticleBlock): boolean => {
      if (!block) return false;
      const isAdBlock = adBlocknames.includes(block.blockName);
      const isImageBlock = !!block.tagAttributes?.className?.includes('wp-block-image');
      const isEmbedBlock = !!block.tagAttributes?.className?.includes('wp-block-embed');
      if (type === 'image') {
        return isImageBlock;
      } else if (type === 'embed') {
        return isEmbedBlock;
      } else if (type === 'ad') {
        return isAdBlock;
      } else {
        return isAdBlock || isImageBlock || isEmbedBlock;
      }
    };

    return this.isNearElement(articleBlocks, index, range, checkBefore, checkAfter, imageOrAdCheck);
  };

  isNearHeader = (
    articleBlocks: ArticleBlock[],
    index: number,
    range: number,
    checkBefore = true,
    checkAfter = false,
  ) => {
    const headerCheck = (block: ArticleBlock): boolean => {
      if (!block) return false;
      const regexHtmlHeadingsElement = /h[1-6]/;
      return block.tag ? regexHtmlHeadingsElement.test(block.tag) : false;
    };

    return this.isNearElement(articleBlocks, index, range, checkBefore, checkAfter, headerCheck);
  };

  isNearHorizontalRule = (
    articleBlocks: ArticleBlock[],
    index: number,
    range: number,
    checkBefore = true,
    checkAfter = false,
  ) => {
    const headerCheck = (block: ArticleBlock): boolean => {
      if (!block) return false;
      return block.tag ? block.tag === 'hr' : false;
    };

    return this.isNearElement(articleBlocks, index, range, checkBefore, checkAfter, headerCheck);
  };

  isNearElement = (
    articleBlocks: ArticleBlock[],
    index: number,
    range: number,
    checkBefore: boolean,
    checkAfter: boolean,
    checkFunc: (block: ArticleBlock) => boolean,
  ): boolean => {
    if (checkFunc(articleBlocks[index])) {
      return true;
    }

    if (checkBefore) {
      for (let i = Math.max(0, index - range); i < index; i++) {
        if (checkFunc(articleBlocks[i])) {
          return true;
        }
      }
    }

    if (checkAfter) {
      for (let i = index + 1; i <= Math.min(articleBlocks.length - 1, index + range); i++) {
        if (checkFunc(articleBlocks[i])) {
          return true;
        }
      }
    }

    return false;
  };

  filterArticleBlocks = (articleBlocks: ArticleBlock[]): ArticleBlock[] => {
    const emptyDivs = ['\n<p></p>\n', '\n<style>\n.adBox { text-align: center; }\n</style>\n'];
    const nonRelevantHtml = [
      '<em>Photo Courtesy:',
      '<strong>Read Next:',
      '<em>Read Next: ',
      '<em>Related News: ',
      '<em>Photo Via Shutterstock</em>',
      '<em>Image: ',
    ];
    const filteredArticleBlocks = [...articleBlocks].filter(articleBlock => {
      if (emptyDivs.includes(articleBlock?.innerHTML)) {
        return false;
      }
      if (!articleBlock?.innerHTML?.trim() && !adBlocknames.includes(articleBlock?.blockName)) {
        return false;
      }
      const hasNonRelevantItems = nonRelevantHtml.some(item => {
        return articleBlock?.innerHTML && articleBlock.innerHTML.includes(item);
      });
      if (hasNonRelevantItems) return false;
      return true;
    });
    return filteredArticleBlocks;
  };

  checkIfShortArticleByBlockCount = (articleBlocks: ArticleBlock[]): boolean => {
    const SHORT_ARTICLE_MAX_BLOCK_LENGTH = 6;
    const filteredArticleBlocks = this.filterArticleBlocks(articleBlocks);
    return filteredArticleBlocks ? filteredArticleBlocks?.length < SHORT_ARTICLE_MAX_BLOCK_LENGTH : false;
  };

  checkIfShortArticleByWordCount = (): boolean => {
    const wordCount = this.props.wordCount;
    if (!wordCount) return false;
    return wordCount < 200;
  };

  checkIfAdBlockIsAtPosition = (articleBlocks: MoneyBlockType[], index: number): boolean => {
    if (!Array.isArray(articleBlocks)) return false;
    const itemBlockName = articleBlocks?.[index]?.blockName;
    const result = itemBlockName ? adBlocknames.includes(itemBlockName) : false;
    return result;
  };

  getAdBlocks = (
    articleBlocks: (MoneyBlockType | CampaignifyPlaceholderBlock)[],
    disableCampaignifyUnit: boolean | undefined,
  ): (MoneyBlockType | CampaignifyPlaceholderBlock)[] => {
    const filteredAdBlocknames = disableCampaignifyUnit
      ? adBlocknames.filter(blockName => blockName !== 'ad/campaignify')
      : adBlocknames;

    const result = articleBlocks.filter(item => filteredAdBlocknames.includes(String(item?.blockName))) as (
      | ArticleBlock
      | CampaignifyPlaceholderBlock
    )[];

    return result;
  };

  injectArticleBetweenCampaigns = (articleBlocks: MoneyBlockType[]): MoneyBlockType[] => {
    const ticker = this.props.ticker;

    if (this.props.strategy !== 'middle-bottom' && this.props.strategy !== 'bottom') {
      // TOP Campaign
      articleBlocks.unshift({
        blockName: 'ad/campaignify',
        nodeID: this.props.nodeId,
        position: 'top',
        strategy: this.props.strategy,
        ticker,
      } as unknown as Block);
    }

    // BOTTOM Campaign
    articleBlocks.push({
      blockName: 'ad/campaignify',
      nodeID: this.props.nodeId,
      position: 'bottom',
      strategy: this.props.strategy,
      ticker,
    } as unknown as Block);

    return articleBlocks;
  };

  injectArticleBlocksAds = (articleBlocks: Blocks, inContentBlocks: Blocks): Blocks => {
    const injectStrategy = this.props.strategy || 'all';

    if (injectStrategy === 'all' || injectStrategy === 'middle' || injectStrategy === 'middle-bottom') {
      const { articleBlocks: injectedCampaignsBlocks } = this.injectCampaignifyInArticleBlocks(articleBlocks);
      articleBlocks = injectedCampaignsBlocks;

      // const { articleBlocks: injectedPrimisBlocks, injectedIndex: primisIndex } = this.injectPrimisInArticleBlocks(
      //   campaignMiddleIndex,
      //   articleBlocks,
      //   this.props.channels,
      //   this.props.tags,
      // );
      // articleBlocks = injectedPrimisBlocks;

      const { articleBlocks: injectedICArticleBlocks } = this.injectICInArticleBlocks(
        articleBlocks,
        inContentBlocks,
        0,
      );
      articleBlocks = injectedICArticleBlocks;
    } else if (injectStrategy === 'external') {
      const placementIndex: number | null = 2;

      const { articleBlocks: injectedICArticleBlocks, injectedIndex } = this.injectICInArticleBlocks(
        articleBlocks,
        inContentBlocks,
        placementIndex,
      );
      articleBlocks = injectedICArticleBlocks;

      // If MiddleBlock right after Ad, inject to the next position.
      let nextIndex = placementIndex + 7;
      if (typeof injectedIndex === 'number' && nextIndex - 1 === injectedIndex) {
        nextIndex += 1;
      }

      const ticker = this.props.ticker;
      const { articleBlocks: injectedBlocks } = this.injectMiddleBlocks(
        articleBlocks,
        {
          blockName: 'ad/campaignify',
          nodeID: this.props.nodeId,
          position: 'middle',
          strategy: this.props.strategy,
          ticker,
        } as unknown as Block,
        nextIndex,
      );
      articleBlocks = injectedBlocks;
    } else if (injectStrategy === 'custom') {
      const placementIndex: number | null = 2;

      const { articleBlocks: injectedICArticleBlocks } = this.injectICInArticleBlocks(
        articleBlocks,
        inContentBlocks,
        placementIndex,
      );

      articleBlocks = injectedICArticleBlocks;
    }

    // Inject between article campaigns at last step to prevent content shift
    // if (
    //   injectStrategy === 'all' ||
    //   injectStrategy === 'between' ||
    //   injectStrategy === 'middle-bottom' ||
    //   injectStrategy === 'external' ||
    //   injectStrategy === 'bottom'
    // ) {
    //   articleBlocks = this.injectArticleBetweenCampaigns(articleBlocks);
    // }

    return articleBlocks;
  };

  injectParsedArticleAds = (parsedParagraphs: string[]): string => {
    let content = this.injectCampaign(parsedParagraphs, 'middle', 5);
    content = this.injectICPlaceholder(content);

    return content.join('');
  };

  injectICInArticleBlocks = (
    articleBlocks: ArticleBlock[] | MoneyBlockType[],
    inContentBlocks: ArticleBlock[] | MoneyBlockType[],
    placeIndex?: number | null,
  ): { injectedIndex: boolean | number; articleBlocks: ArticleBlock[] | MoneyBlockType[] } => {
    try {
      let injectedIndex: boolean | number = false;

      const insertBlockAtTheBottomOfArticle = blocks => {
        blocks.forEach(block => {
          articleBlocks.push(block);
        });
      };

      const googleAdPlaceholder: Block = this.props.googleAdPlaceholder || getAdBlock();

      const hasGoogleAd =
        Array.isArray(inContentBlocks) && inContentBlocks?.some(item => item.blockName === 'acf/google-ad-placement');

      let injectBlocks = hasGoogleAd ? [...inContentBlocks] : [...inContentBlocks, googleAdPlaceholder];

      if (!this.props.hasAdLight) {
        const freestarAdPlaceholder = getDisplayAdBlock();

        if (this.props.raptiveEnabled) {
          injectBlocks = [...inContentBlocks, freestarAdPlaceholder];
        }
      }

      injectBlocks = injectNodeIdInCampaignBlocks(
        injectBlocks as unknown as Block[],
        'in-article-ga-placeholder',
        this.props.nodeId as string,
      );

      // const investingChannelBlock = {
      //   attrs: {
      //     data: {
      //       id: "in-article-ic-placeholder",
      //       mobileSize: "3x7,300x250,fluid"
      //     }
      //   },
      //   blockName: "ad/investing-channel"
      // };

      if (placeIndex) {
        if (articleBlocks.length - (placeIndex + 1) < 3) {
          insertBlockAtTheBottomOfArticle(injectBlocks);
        } else {
          const placeholderPosition = findPlaceholderPlaceInBlocks(articleBlocks, placeIndex, injectBlocks.length);

          if (placeholderPosition === false) {
            insertBlockAtTheBottomOfArticle(injectBlocks);
          }

          if (typeof placeholderPosition === 'number') {
            injectedIndex = placeholderPosition + (injectBlocks?.length - 1); // index calculated based on injected blocks length
            articleBlocks.splice(placeholderPosition, 0, ...injectBlocks);
          }
        }
      } else {
        insertBlockAtTheBottomOfArticle(injectBlocks);
      }

      return { articleBlocks, injectedIndex };
    } catch {
      return { articleBlocks, injectedIndex: 0 };
    }
  };

  injectMiddleBlocks = (
    articleBlocks: ArticleBlocksList[],
    block: ArticleBlocksList,
    placementIndex?: number,
  ): { indexInserted: number | null; articleBlocks: MoneyBlockType[] | ArticleBlock[] } => {
    try {
      const { index: indexInserted, modifiedBlocks } = insertCampaignInBlocks(
        articleBlocks,
        block as Block,
        placementIndex,
      );

      articleBlocks = modifiedBlocks;

      return { articleBlocks, indexInserted };
    } catch {
      return { articleBlocks, indexInserted: 0 };
    }
  };

  injectCampaignifyInArticleBlocks = (
    articleBlocks: ArticleBlocksList[],
    placementIndex?: number,
  ): { indexInserted: number | null; articleBlocks: MoneyBlockType[] | ArticleBlock[] } => {
    try {
      const ticker = this.props.ticker;
      const { index: indexInserted, modifiedBlocks } = insertCampaignInBlocks(
        articleBlocks,
        {
          blockName: 'ad/campaignify',
          nodeID: (this.props.nodeId as number) ?? 0,
          position: 'middle',
          strategy: this.props.strategy,
          ticker,
        } as Block,
        placementIndex,
      );

      articleBlocks = modifiedBlocks;

      return { articleBlocks, indexInserted };
    } catch {
      return { articleBlocks, indexInserted: 0 };
    }
  };

  injectPrimisInArticleBlocks = (placeIndex, articleBlocks, channels, tags) => {
    try {
      if (this.props.primis === false) {
        return { articleBlocks, injectedIndex: placeIndex };
      }

      let primisIndex: number | null = null;

      const primisBlock = {
        blockName: 'ad/primis',
        id: getPrimisIdByTaxonomies(channels, tags),
      };

      if (placeIndex) {
        primisIndex = placeIndex + 1; // insert one placement after
        articleBlocks.splice(primisIndex, 0, primisBlock);
      } else {
        const { index, modifiedBlocks } = insertCampaignInBlocks(articleBlocks, primisBlock);
        primisIndex = index;
        articleBlocks = modifiedBlocks;
      }

      return { articleBlocks, injectedIndex: primisIndex };
    } catch {
      return { articleBlocks, injectedIndex: 0 };
    }
  };

  injectICPlaceholder = (parsedContent: string[]): string[] => {
    if (this.props.raptiveEnabled) return parsedContent;

    const investingChannel = ReactDOMServer.renderToString(
      <GoogleAdUnit adUnit="DotCom_300x250_InArticle" dfpNetworkId="4107070" sizes={[[300, 250]]} />,
    );

    const bzPrimisVideoIndex = parsedContent.findIndex(item => {
      return item.includes('primis-video');
    });

    const insertAtTheBottomOfArticle = () => {
      return insertAt(parsedContent.length - 1, investingChannel, parsedContent);
    };

    if (parsedContent.length - (bzPrimisVideoIndex + 1) < 3) {
      return insertAtTheBottomOfArticle();
    } else {
      const placeholderPosition = findPlaceholderPositionInParsedContent(parsedContent, bzPrimisVideoIndex, 3);

      if (placeholderPosition === false) {
        return insertAtTheBottomOfArticle();
      }

      return insertAt(placeholderPosition, investingChannel, parsedContent);
    }
  };

  injectPrimisVideo = (parsedContent: string[]): string[] => {
    const primis = ReactDOMServer.renderToString(
      <Primis id={this.getPrimisIdByTaxonomies()} placeholderId="primis-article-video" />,
    );

    const bzCampaignIndex = parsedContent.findIndex(item => {
      return item.includes('bz-campaign-middle');
    });

    if (bzCampaignIndex > 0) {
      return insertAt(bzCampaignIndex + 1, primis, parsedContent);
    }

    return insertCampaign(parsedContent, primis) as string[];
  };

  injectCampaign = (
    parsedContent: string[],
    placement: 'top' | 'middle' | 'bottom' = 'middle',
    index: number,
  ): string[] => {
    const { campaigns } = this.props;
    if (this.props.raptiveEnabled && placement !== 'middle') {
      return parsedContent;
    }

    if (campaigns && Object.keys(campaigns).length > 0 && campaigns[placement]) {
      const article = insertCampaign(
        parsedContent,
        `<div class="bz-campaign bz-campaign-${placement}">${campaigns[placement]}</div>`,
        index,
      ) as string[];

      return article;
    }
    return parsedContent;
  };

  renderCampaign = (campaign?: string): ReactElement | null => {
    if (!campaign || this.props.isSponsored) {
      return null;
    }

    const content = performRegexesOnCampaignHTML(this.props.isAmp ? campaign : sanitizeHTML(campaign));

    return <div className="bz-campaign" dangerouslySetInnerHTML={{ __html: content }} />;
  };

  render() {
    const { children } = this.props;
    const articleBodyWithCampaign = this.formatBodyContent();

    return <div className={styles.root}>{children(articleBodyWithCampaign)}</div>;
  }
}
